import { ChangeDetectorRef, Component, computed, inject, input, OnChanges, OnInit, output, signal, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogClose, MatDialogRef } from '@angular/material/dialog';
import { Budget } from '../../models/budget.model';
import { MatToolbar } from '@angular/material/toolbar';
import { MatIcon } from '@angular/material/icon';
import { HumanPipe } from '../../shared/pipes/human.pipe';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatButton, MatIconButton, MatMiniFabButton } from '@angular/material/button';
import { BudgetService } from '../budget.service';
import { catchError, EMPTY, map } from 'rxjs';
import { CheckIn } from '../../models/checkin.model';
import { ImageUploaderComponent } from '../../shared/image-uploader/image-uploader.component';
import { AdminService } from '../../admin/services/admin.service';
import { UserService } from '../../services/user.service';
import { rxResource } from '@angular/core/rxjs-interop';
import { ConfirmDialogComponent } from '../../main/confirm-dialog/confirm-dialog.component';
import { Tag } from '../../models/tag.model';
import { generateInventoryActionFormGroup } from '../../models/inventory_action.model';
import { MatSuffix } from '@angular/material/form-field';
import { MatTooltip } from '@angular/material/tooltip';
import { InventoryActionFormComponent } from '../../shared/common-forms/inventory-action-form/inventory-action-form.component';
import { FormGroupPipe } from '../../shared/pipes/form-group.pipe';
import { MoneyPipe } from '../../shared/pipes/money.pipe';
import { NgClass, UpperCasePipe } from '@angular/common';
import { fromProductToInventoryActionProduct } from '../../models/inventory-action-product';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-budget-form',
  templateUrl: './budget-form.component.html',
  styleUrl: './budget-form.component.scss',
  imports: [
    FormGroupPipe,
    HumanPipe,
    ImageUploaderComponent,
    InventoryActionFormComponent,
    MatButton,
    MatIconButton,
    MatDialogClose,
    MatIcon,
    MatMiniFabButton,
    MatProgressSpinner,
    MatToolbar,
    MatTooltip,
    MatIcon,
    MatSuffix,
    MoneyPipe,
    NgClass,
    UpperCasePipe
  ],
  host: {
    '[class.chat]': 'this.chatMode()',
  },
})
export class BudgetFormComponent implements OnInit {

  private readonly adminService = inject(AdminService);

  private readonly budgetService = inject(BudgetService);

  private readonly userService = inject(UserService);

  private readonly data = inject(MAT_DIALOG_DATA);

  chatMode = input<boolean>(false);

  sourceDocument = input<Partial<CheckIn>>(this.data?.sourceDocument);

  sourceDocumentType = input<string>(this.data?.sourceDocumentType);

  private readonly dialogRef = inject(MatDialogRef<BudgetFormComponent>);

  private readonly dialog = inject(MatDialog);

  public isDialogMode = !!this.dialogRef.id;

  private readonly fb = inject(FormBuilder);

  public formGroup = input<FormGroup>(this.fb.group({
    document_id: this.fb.control<number | null>(null),
    document_type: this.fb.control<string | null>(null),
    pictures: this.fb.array([]),
    inventory_actions: this.fb.array([]),
  }));

  public saving = signal<boolean>(false);

  public isExpandedMode: boolean = this.adminService.local$.getValue()?.preferences?.budget_type === 'expanded';

  public resourceTagCategories = rxResource({
    stream: () => this.budgetService.tags({
      extended: 'true',
      checkForDiscounts: 'true',
      requirementId: this.sourceDocument().requirement_id
    }).pipe(map((r) => r.body))
  }
  );

  public tags = computed(() => this.resourceTagCategories.value()?.tags || []);

  public categories = computed(() => this.resourceTagCategories.value()?.categories || []);

  public currentUser = this.userService.user$.getValue();

  private readonly cdr = inject(ChangeDetectorRef);

  public totals: Record<number, number> = {};

  public total = signal<number>(0);

  public tax = this.adminService.local$.getValue()?.country?.preferences?.tax_rate || 0;

  private readonly snackBar = inject(MatSnackBar);

  cancel = output();


  ngOnInit(): void {
    this.formGroup().patchValue({
      document_id: this.sourceDocument().id,
      document_type: this.sourceDocumentType(),
    });
  }

  public get inventoryActionFormArray() {
    return this.formGroup().get('inventory_actions') as FormArray;
  }

  public get picturesFormArray(): FormArray {
    return this.formGroup().get('pictures') as FormArray;
  }

  public set picturesFormArray(pictures: any[]) {
    this.picturesFormArray.clear();
    pictures.forEach((picture) => {
      this.picturesFormArray.push(this.fb.control(picture));
    });
  }

  public get picturesInGallery() {
    return (this.sourceDocument().order_attachments|| []).concat(this.sourceDocument().pictures);
  }

  get discountTotal() {
    return this.inventoryActionFormArray.getRawValue()
      .map((inventoryAction) => inventoryAction.inventory_action_products.map(
        (product) => product.amount * ((product.discount_percent || 0) / 100) * product.quantity,
      ).reduce((a, b) => a + b, 0)).reduce((a, b) => a + b, 0);
  }

  calculateValues() {
    const categoryIds = this.tags().map((t) => t.category_ids).flat();
    const categories = this.categories().filter((c) => categoryIds.includes(c.id));

    categories.forEach((category) => {
      this.totals[category.id] = 0;
    });
    this.inventoryActionFormArray.getRawValue().filter((ia) => !ia.status.failed).forEach((inventoryAction) => {
      inventoryAction.inventory_action_products
        .filter((product) => !product._destroy)
        .forEach((product) => {
          if (!this.totals[product.category_id]) {
            this.totals[product.category_id] = product.amount * product.quantity;
          } else {
            this.totals[product.category_id] += product.amount * product.quantity;
          }
        });
    });

    this.total.set(Object.values(this.totals).reduce((a, b) => a + b, 0) - this.discountTotal);
  }

  public setImage(pictures) {
    this.picturesFormArray = pictures;
  }

  public getTag(tag_id: number) {
    return this.tags().find((t) => t.id === tag_id);
  }

  public getTagCategories(tag_id: number) {
    return this.getTag(tag_id)?.category_ids;
  }

  public addInventoryAction(tag: Tag) {
    const inventoryActionProducts = this.categories().map((category) => {
      if (!category.default_product || !tag.category_ids.includes(category.id)) {
        return null;
      }

      return fromProductToInventoryActionProduct(
        category.default_product,
        category.id
      );
    }).filter((p) => p !== null);


    this.inventoryActionFormArray.push(
      generateInventoryActionFormGroup(
        {
          type: 'InventoryAction::Consume',
          inventory_action_products: inventoryActionProducts,
          warehouse_id: this.sourceDocument().warehouse_id,
          tag_id: tag?.id,
          owner_id: this.currentUser.id,
          owner_type: 'User',
        },
        {
          include_comments: true,
          include_document: true,
          include_owner: true,
          include_pictures: true,
          include_status: true,
          include_tag: true,
        }
      ),
      { emitEvent: false }
    );
    this.calculateValues();
  }

  public removeItem(index) {
    this.dialog.open(ConfirmDialogComponent, {
      data: { title: 'Eliminar producto', body: '' },
    }).afterClosed().subscribe((result) => {
      if (!result) return;

      this.inventoryActionFormArray.removeAt(index);
      this.calculateValues();
      this.cdr.detectChanges();
    });
  }

  public setImagesToInventoryAction(formGroup, pictures: any[]) {
    formGroup.controls.pictures = this.fb.array(pictures);
  }

  duplicateInventoryAction(index: number) {

    const { id, ...original } = this.inventoryActionFormArray.at(index).getRawValue();

    this.inventoryActionFormArray.push(generateInventoryActionFormGroup(
      original,
      {
        include_comments: true,
        include_document: true,
        include_pictures: true,
        include_status: true,
        include_tag: true,
      })
    );

    this.calculateValues();
      
  }

  public saveForm(): void {
    const payload = this.formGroup().getRawValue();
    payload.inventory_actions_attributes = payload.inventory_actions.map((action) => ({
      ...action,
      company_id: this.adminService.local$.getValue().id,
      inventory_action_products_attributes: action.inventory_action_products,
      inventory_action_products: undefined,
    }));
    payload.inventory_actions = undefined

    this.saving.set(true);

    this.budgetService.create(payload)
    .pipe(
      catchError(() => {
        this.saving.set(false);
        this.snackBar.open('Error al guardar el presupuesto', 'Cerrar', {
          duration: 3000,
          panelClass: 'snackbar-error',
        });
        return EMPTY;
      }),
    )
    .subscribe((response) => {
      this.dialogRef.close(response.body as Budget);
    });
  }
}
